import React from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { IDifferencePositions } from '@entities/purchase/model/index.types';
import { formatPrice } from '@shared/helpers';
import { Flex, Tooltip, UiText } from '@shared/ui';

import cls from './ChartBarTooltip.module.css';

interface IChartBarTooltipProps {
    differenceBasicPositions: IDifferencePositions;
    differenceExcessPositions: IDifferencePositions;
    currency: string;
}
export const ChartBarTooltip = ({
    differenceBasicPositions,
    differenceExcessPositions,
    currency = '₽',
}: IChartBarTooltipProps) => {
    const { t } = useTranslation();

    const differenceBasicPositionsType = differenceBasicPositions?.type;
    const differenceExcessPositionsType = differenceExcessPositions?.type;
    const differenceBasicPositionsSum = differenceBasicPositions?.sum;
    const differenceExcessPositionsSum = differenceExcessPositions?.sum;
    const differenceBasicPositionsPercent = differenceBasicPositions?.percent;
    const differenceExcessPositionsPercent = differenceExcessPositions?.percent;

    const differenceBasicPositionsTypeStyles = cn(
        differenceBasicPositionsType === 'saving' && differenceBasicPositionsSum > 0
            ? cls.Economy
            : differenceBasicPositionsType === 'overspending' && differenceBasicPositionsSum > 0
            ? cls.Overspend
            : cls.Title
    );
    const differenceExcessPositionsTypeStyles = cn(
        differenceExcessPositionsType === 'saving' && differenceExcessPositionsSum > 0
            ? cls.Economy
            : differenceExcessPositionsType === 'overspending' && differenceExcessPositionsSum > 0
            ? cls.Overspend
            : cls.Title
    );

    return (
        <Tooltip classes={cls.ChartBarTooltip}>
            <Flex direction="column" fullWidth>
                <Flex gap={8} justify="between" fullWidth>
                    <UiText variant="mediumPlain" className={cls.Title}>
                        {differenceBasicPositionsType === 'saving' &&
                            t('PurchaseChart.ChartBarTooltip.saving.text')}
                        {differenceBasicPositionsType === 'overspending' &&
                            t('PurchaseChart.ChartBarTooltip.overspending.text')}
                    </UiText>
                    <Flex gap={4}>
                        <UiText
                            variant="subPlain"
                            weight="medium"
                            className={differenceBasicPositionsTypeStyles}
                        >
                            {formatPrice(differenceBasicPositionsSum, {
                                maximumFractionDigits: 2,
                                currencySign: currency,
                            })}
                        </UiText>
                        <UiText variant="subPlain" className={differenceBasicPositionsTypeStyles}>
                            (
                            {formatPrice(differenceBasicPositionsPercent, {
                                maximumFractionDigits: 2,
                            })}
                            %)
                        </UiText>
                    </Flex>
                </Flex>
                <Flex gap={8} justify="between" fullWidth>
                    <UiText variant="mediumPlain" className={cls.Title}>
                        {differenceExcessPositionsType === 'saving' &&
                            t('PurchaseChart.ChartBarTooltip.ExcessSaving.text')}
                        {differenceExcessPositionsType === 'overspending' &&
                            t('PurchaseChart.ChartBarTooltip.ExcessOverspending.text')}
                    </UiText>
                    <Flex gap={4}>
                        <UiText
                            variant="subPlain"
                            weight="medium"
                            className={differenceExcessPositionsTypeStyles}
                        >
                            {formatPrice(differenceExcessPositionsSum, {
                                maximumFractionDigits: 2,
                                currencySign: currency,
                            })}
                        </UiText>
                        <UiText variant="subPlain" className={differenceExcessPositionsTypeStyles}>
                            (
                            {formatPrice(differenceExcessPositionsPercent, {
                                maximumFractionDigits: 2,
                            })}
                            %)
                        </UiText>
                    </Flex>
                </Flex>
            </Flex>
        </Tooltip>
    );
};
